import IconMonitoring from '@picsart/icons/IconMonitoring';
import IconExportVideo from '@picsart/icons/IconExportVideo';
import IconColorPicker from '@picsart/icons/IconColorPicker';
import IconRatio4x3 from '@picsart/icons/IconRatio4x3';
import IconPDF from '@picsart/icons/IconPDF';
import IconAITools from '@picsart/icons/IconAITools';
import IconCanvas from '@picsart/icons/IconCanvas';
import IconReduce from '@picsart/icons/IconReduce';
import IconFill from '@picsart/icons/IconFill';
import IconTextStyles from '@picsart/icons/IconTextStyles';
import IconFonts from '@picsart/icons/IconFonts';
import IconGlow from '@picsart/icons/IconGlow';
import IconFaceTool from '@picsart/icons/IconFaceTool';
import IconHSL from '@picsart/icons/IconHSL';
import IconCutout from '@picsart/icons/IconCutout';
import IconFrameTrim from '@picsart/icons/IconFrameTrim';
import IconColorExposure from '@picsart/icons/IconColorExposure';
import IconAddVideo from '@picsart/icons/IconAddVideo';
import IconObjectResize from '@picsart/icons/IconObjectResize';
import IconEditPhoto from '@picsart/icons/IconEditPhoto';
import IconTextStaff from '@picsart/icons/IconTextStaff';
import IconBlankWrite from '@picsart/icons/IconBlankWrite';
import IconFaceMask from '@picsart/icons/IconFaceMask';
import IconRefresh from '@picsart/icons/IconRefresh';
import IconEmail from '@picsart/icons/IconEmail';
import IconCreditCard from '@picsart/icons/IconCreditCard';
import IconHashtag from '@picsart/icons/IconHashtag';
import IconRotate from '@picsart/icons/IconRotate';
import IconCrop from '@picsart/icons/IconCrop';
import IconInstagram from '@picsart/icons/IconInstagram';
import IconEmbedCode from '@picsart/icons/IconEmbedCode';
import IconList from '@picsart/icons/IconList';
import IconAI from '@picsart/icons/IconAI';
import IconLearn from '@picsart/icons/IconLearn';
import IconBlogBanner from '@picsart/icons/IconBlogBanner';
import IconBlog from '@picsart/icons/IconBlog';
import IconEmailHeader from '@picsart/icons/IconEmailHeader';
import IconMoveToFolder from '@picsart/icons/IconMoveToFolder';
import IconNoResult from '@picsart/icons/IconNoResult';
import IconFlyer from '@picsart/icons/IconFlyer';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import AdMakerLogo from '@quicktools/shared-components/data/tools/AdMakerLogo';

import { getNavLink } from '../../utils';
import { ICON_COLOR, ICON_BACKGROUND, ICON_FILL } from '../colors';
import { Calendar } from '../../components/core/svgicons';

import { IToolCategory, IToolData, ToolCategory } from './types';

const getToolCategoryName = (category: ToolCategory) => `${category} Tools`;

export const ADMAKER_TOOL = {
  id: 'admaker',
  name: 'Admaker',
  description:
    'Create branded product ads for social media and beyond with the power of generative AI. ',
  href: 'https://onlineadmaker.com/',
  icon: {
    src: AdMakerLogo,
    noColor: true,
    width: 24,
    height: 24,
  },
  categories: [ToolCategory.Design],
  isNew: true,
  openInNewTab: true,
  showLabelOnNavBar: true,
};

export const IMAGE_MIRROR_TOOL = {
  id: 'image_mirror',
  name: 'Image Mirror',
  description:
    'Mirror your image to upgrade your social media feed or elevate your creative designs.',
  href: getNavLink('/image/mirror'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.magenta],
    fill: ICON_FILL[ICON_COLOR.magenta],
    src: IconCanvas,
  },
  categories: [ToolCategory.Image],
};

export const IMAGE_CROP_TOOL = {
  id: 'image_crop',
  name: 'Crop Image',
  description:
    'Is your image too big? Use our social media presets or crop it yourself with our Crop Image tool.',
  href: getNavLink('/image/crop'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.magenta],
    fill: ICON_FILL[ICON_COLOR.magenta],
    src: IconCrop,
  },
  categories: [ToolCategory.Image],
};

export const IMAGE_ROTATE_TOOL = {
  id: 'image_rotate',
  name: 'Rotate Image',
  description:
    'Easily rotate any image for social media, messages, document or share it with your friends.',
  href: getNavLink('/image/rotate'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.magenta],
    fill: ICON_FILL[ICON_COLOR.magenta],
    src: IconRotate,
  },
  categories: [ToolCategory.Image],
};

export const PDF_TO_JPG_TOOL = {
  id: 'pdf_to_jpg',
  name: 'PDF to JPG',
  description:
    'Convert a PDF to JPG without any software to download. Our file conversion service is free to use.',
  href: getNavLink('/convert/pdf-to-jpg'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.Convert, ToolCategory.PDF],
};

export const HEIC_TO_JPG_TOOL = {
  id: 'heic_to_jpg',
  name: 'HEIC to JPG',
  description: 'Convert HEIC photos to JPG in no time with our file converter.',
  href: getNavLink('/convert/heic-to-jpg'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.green],
    fill: ICON_FILL[ICON_COLOR.green],
    src: IconCanvas,
  },
  categories: [ToolCategory.Convert, ToolCategory.Image],
};

export const PNG_TO_SVG_TOOL = {
  id: 'png_to_svg',
  name: 'PNG to SVG',
  description:
    'Convert your PNG images into SVG format in a snap. Use our easy-to-use PNG to SVG converter for free.',
  href: getNavLink('/convert/png-to-svg'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.green],
    fill: ICON_FILL[ICON_COLOR.green],
    src: IconCanvas,
  },
  categories: [ToolCategory.Convert, ToolCategory.Image],
};

export const BACKGROUND_REMOVER_TOOL = {
  id: 'background_remover',
  name: 'Background Remover',
  description: 'Remove the background easily and create a transparent background for your photo.',
  href: getNavLink('/image/background-remover'),
  widgetUrl: getNavLink('/widget/background-remover'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.purple],
    fill: ICON_FILL[ICON_COLOR.purple],
    src: IconAITools,
  },
  categories: [ToolCategory.Image, ToolCategory.Design],
};

export const RESIZE_IMAGE_TOOL = {
  id: 'resize_image',
  name: 'Resize Image',
  description: 'Resize images without cropping or losing the quality of the original image.',
  href: getNavLink('/image/resize'),
  icon: {
    src: IconObjectResize,
    background: ICON_BACKGROUND[ICON_COLOR.magenta],
    fill: ICON_FILL[ICON_COLOR.magenta],
  },
  categories: [ToolCategory.Image],
};

export const COMPRESS_IMAGE_TOOL = {
  id: 'compress_image',
  name: 'Compress Image',
  description: 'The easiest and fastest way to compress your JPEGs, PNGs, and any other images.',
  href: getNavLink('/image/compress'),
  widgetUrl: getNavLink('/widget/image-compress'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.magenta],
    fill: ICON_FILL[ICON_COLOR.magenta],
    src: IconReduce,
  },
  categories: [ToolCategory.Image],
};

export const JPG_TO_PDF_TOOL = {
  id: 'jpg_to_pdf',
  name: 'JPG to PDF',
  description: 'Convert JPG images to PDF in seconds. Easily adjust orientation and margins, too.',
  href: getNavLink('/convert/jpg-to-pdf'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.Convert, ToolCategory.PDF],
};

export const PNG_TO_PDF_TOOL = {
  id: 'png_to_pdf',
  name: 'PNG to PDF',
  description: 'Convert PNG images to PDF. It only takes seconds with our free online converter.',
  href: getNavLink('/convert/png-to-pdf'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.Convert, ToolCategory.PDF],
};

export const COLOR_WHEEL_TOOL = {
  id: 'color_wheel',
  name: 'Color Wheel',
  description:
    'Create coordinated color themes by finding complementary colors on our color wheel tool.',
  href: getNavLink('/color/wheel'),
  icon: {
    src: IconHSL,
    background: ICON_BACKGROUND[ICON_COLOR.indigo],
    fill: ICON_FILL[ICON_COLOR.indigo],
  },
  categories: [ToolCategory.Color],
};

export const COLOR_PALETTE_TOOL = {
  id: 'color_palette',
  name: 'Color Palette',
  description:
    'Find a coordinated color scheme or trending color combinations just by uploading a photo. ',
  href: getNavLink('/color/palette-generator'),
  icon: {
    src: IconFill,
    background: ICON_BACKGROUND[ICON_COLOR.indigo],
    fill: ICON_FILL[ICON_COLOR.indigo],
  },
  categories: [ToolCategory.Color, ToolCategory.Design],
};

export const MERGE_PDF_TOOL = {
  id: 'merge_pdf',
  name: 'Merge PDF',
  description: 'Merge and combine multiple PDF files into a single PDF document.',
  href: getNavLink('/pdf/merge'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.PDF],
};

export const SPLIT_PDF_TOOL = {
  id: 'split_pdf',
  name: 'Split PDF',
  description: 'Extract pages from your PDF file or split a PDF into multiple files.',
  href: getNavLink('/pdf/split'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.PDF],
};

export const FONT_GENERATOR_TOOL = {
  id: 'font_generator',
  name: 'Font Generator',
  description:
    'Generate stylish fonts and fancy font styles for your own text to put in your social bios and more. ',
  href: getNavLink('/text/font-generator'),
  icon: {
    fill: ICON_FILL[ICON_COLOR.blue],
    src: IconTextStyles,
    width: 20,
    height: 20,
    background: ICON_BACKGROUND[ICON_COLOR.blue],
  },
  categories: [ToolCategory.Text],
  subCategories: [
    { label: 'All', id: '', href: getNavLink('/text/font-generator') },
    {
      label: 'Cool',
      id: 'cool',
      href: getNavLink('/text/font-generator/cool'),
    },
    {
      label: 'Fancy',
      id: 'fancy',
      href: getNavLink('/text/font-generator/fancy'),
    },
    {
      label: 'Cursive',
      id: 'cursive',
      href: getNavLink('/text/font-generator/cursive'),
    },
    {
      label: 'Small',
      id: 'small',
      href: getNavLink('/text/font-generator/small'),
    },
    {
      label: 'Bold',
      id: 'bold',
      href: getNavLink('/text/font-generator/bold'),
    },
    {
      label: 'Glitch',
      id: 'glitch',
      href: getNavLink('/text/font-generator/glitch'),
    },
    {
      label: 'Letter cases',
      id: 'letter-case',
      href: getNavLink('/text/font-generator/case-converter'),
    },
    {
      label: 'Symbol Text',
      id: 'symbols',
      href: getNavLink('/text/font-generator/symbols'),
    },
    {
      label: 'Text art',
      id: 'text-art',
      href: getNavLink('/text/font-generator/text-art'),
    },
  ],
};

export const WORD_COUNT_TOOL = {
  id: 'word_counter',
  name: 'Word Counter',
  description:
    'Get a count of the number of characters and words (with or without spaces) in your document.',
  href: getNavLink('/text/word-counter'),
  icon: {
    src: IconFonts,
    background: ICON_BACKGROUND[ICON_COLOR.blue],
    fill: ICON_FILL[ICON_COLOR.blue],
  },
  categories: [ToolCategory.Text],
};

export const SYMBOLS_TOOL = {
  id: 'symbols',
  name: 'Symbols',
  description:
    'Look up amazing unicode Symbols in our library. Filter, search and paste to any platform.',
  href: getNavLink('/text/symbols'),
  icon: {
    src: IconGlow,
    background: ICON_BACKGROUND[ICON_COLOR.blue],
    fill: ICON_FILL[ICON_COLOR.blue],
  },
  categories: [ToolCategory.Text],
  subCategories: [
    { label: 'All', id: 'all', href: getNavLink('/text/symbols') },
    { label: 'Hearts', id: 'hearts', href: getNavLink('/text/symbols/hearts') },
    { label: 'Music', id: 'music', href: getNavLink('/text/symbols/music') },
    {
      label: 'Mathematical',
      id: 'math',
      href: getNavLink('/text/symbols/math'),
    },
    { label: 'Arrows', id: 'arrows', href: getNavLink('/text/symbols/arrows') },
    { label: 'Stars', id: 'stars', href: getNavLink('/text/symbols/stars') },
    {
      label: 'Currency',
      id: 'currencies',
      href: getNavLink('/text/symbols/currencies'),
    },
    {
      label: 'Smileys',
      id: 'smileys',
      href: getNavLink('/text/symbols/smileys'),
    },
    {
      label: 'Weather',
      id: 'weather',
      href: getNavLink('/text/symbols/weather'),
    },
    {
      label: 'Roman Numerals',
      id: 'numerals',
      href: getNavLink('/text/symbols/numerals'),
    },
  ],
};

export const EMOJIS_TOOL = {
  id: 'emojis',
  name: 'Emojis',
  description:
    'Find the perfect emoji for any occasion and see what it will look like on other devices.',
  href: getNavLink('/text/emojis'),
  icon: {
    src: IconFaceTool,
    background: ICON_BACKGROUND[ICON_COLOR.blue],
    fill: ICON_FILL[ICON_COLOR.blue],
  },
  categories: [ToolCategory.Text],
  subCategories: [
    { label: 'All', id: 'all', href: getNavLink('/text/emojis') },
    {
      label: 'Smileys & People',
      id: 'people',
      href: getNavLink('/text/emojis/people'),
    },
    {
      label: 'Animals & Nature',
      id: 'nature',
      href: getNavLink('/text/emojis/nature'),
    },
    {
      label: 'Food & Drink',
      id: 'food-drink',
      href: getNavLink('/text/emojis/food-drink'),
    },
    {
      label: 'Travel & Places',
      id: 'travel-places',
      href: getNavLink('/text/emojis/travel-places'),
    },
    {
      label: 'Activities',
      id: 'activities',
      href: getNavLink('/text/emojis/activities'),
    },
    {
      label: 'Objects',
      id: 'objects',
      href: getNavLink('/text/emojis/objects'),
    },
    {
      label: 'Symbols',
      id: 'symbols',
      href: getNavLink('/text/emojis/symbols'),
    },
    { label: 'Flags', id: 'flags', href: getNavLink('/text/emojis/flags') },
  ],
};

export const PROFILE_PICTURE_TOOL = {
  id: 'profile_picture_creator',
  name: 'Profile Picture Maker',
  description:
    'Turn your selfie into a polished profile picture and download in the perfect size for any platform.',
  href: getNavLink('/design/profile-picture'),
  icon: {
    src: IconFaceTool,
    background: ICON_BACKGROUND[ICON_COLOR.purple],
    fill: ICON_FILL[ICON_COLOR.purple],
  },
  categories: [ToolCategory.Image, ToolCategory.Design],
};

export const TRIM_VIDEO_TOOL = {
  id: 'trim_video',
  name: 'Trim Video',
  description: 'Create video clips easily with our online video trimming tool.',
  href: getNavLink('/video/trim'),
  icon: {
    src: IconCutout,
    background: ICON_BACKGROUND[ICON_COLOR.yellow],
    fill: ICON_FILL[ICON_COLOR.yellow],
  },
  categories: [ToolCategory.Video],
};

export const CROP_VIDEO_TOOL = {
  id: 'crop_video',
  name: 'Crop Video',
  description:
    'Just upload your file and use our video cropper to adjust it to any size and social media platform.',
  href: getNavLink('/video/crop'),
  icon: {
    src: IconFrameTrim,
    background: ICON_BACKGROUND[ICON_COLOR.yellow],
    fill: ICON_FILL[ICON_COLOR.yellow],
  },
  categories: [ToolCategory.Video],
};

export const COLOR_PICKER_TOOL = {
  id: 'color_picker',
  name: 'Color Picker',
  description:
    'Have a color in mind but don’t know it’s code? Select from a gradient and copy HEX, RGB and CMYK result.',
  href: getNavLink('/color/picker'),
  icon: {
    src: IconColorPicker,
    background: ICON_BACKGROUND[ICON_COLOR.indigo],
    fill: ICON_FILL[ICON_COLOR.indigo],
    width: 16,
    height: 16,
  },
  categories: [ToolCategory.Color],
};

export const COLOR_CODES_TOOL = {
  id: 'color_codes',
  name: 'Color Codes',
  description:
    'Take your color or color code and convert it. HEX, RYB, CMYK and more at the click of a button.',
  href: getNavLink('/color/codes'),
  icon: {
    src: IconColorExposure,
    background: ICON_BACKGROUND[ICON_COLOR.indigo],
    fill: ICON_FILL[ICON_COLOR.indigo],
    width: 20,
    height: 20,
  },
  categories: [ToolCategory.Color],
};

export const MOV_TO_MP4_TOOL = {
  id: 'mov_to_mp4',
  name: 'MOV to MP4',
  description: 'Simply upload your MOV video and download your clip in MP4 format.',
  href: getNavLink('/convert/mov-to-mp4'),
  icon: {
    src: IconAddVideo,
    background: ICON_BACKGROUND[ICON_COLOR.yellow],
    fill: ICON_FILL[ICON_COLOR.yellow],
  },
  categories: [ToolCategory.Video, ToolCategory.Convert],
};

export const WEBM_TO_MP4_TOOL = {
  id: 'webm_to_mp4',
  name: 'WebM to MP4',
  description: 'Simply upload your WebM video and download your clip in MP4 format.',
  href: getNavLink('/convert/webm-to-mp4'),
  icon: {
    src: IconAddVideo,
    background: ICON_BACKGROUND[ICON_COLOR.yellow],
    fill: ICON_FILL[ICON_COLOR.yellow],
  },
  categories: [ToolCategory.Video, ToolCategory.Convert],
};

export const MKV_TO_MP4_TOOL = {
  id: 'mkv_to_mp4',
  name: 'MKV to MP4',
  description: 'Simply upload your MKV video and download your clip in MP4 format.',
  href: getNavLink('/convert/mkv-to-mp4'),
  icon: {
    src: IconAddVideo,
    background: ICON_BACKGROUND[ICON_COLOR.yellow],
    fill: ICON_FILL[ICON_COLOR.yellow],
  },
  categories: [ToolCategory.Video, ToolCategory.Convert],
};

export const CALENDAR_MAKER_TOOL = {
  id: 'calendar_maker',
  name: 'Calendar Maker',
  description:
    'Select your date range and style, then print or download stylish calendars for any occasion.',
  href: getNavLink('/design/calendar'),
  icon: {
    src: Calendar,
    background: ICON_BACKGROUND[ICON_COLOR.purple],
    fill: ICON_FILL[ICON_COLOR.purple],
  },
  categories: [ToolCategory.Design],
};

export const COMPRESS_PDF_TOOL = {
  id: 'compress_pdf',
  name: 'Compress PDF',
  description:
    'Reduce the size of your PDF file online in a single click. Fast, easy & completely free!',
  href: getNavLink('/pdf/compress'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.PDF],
};

export const WORD_TO_PDF_TOOL = {
  id: 'word_to_pdf',
  name: 'Word to PDF',
  description: 'The most effortless way to convert your DOC and DOCX files to PDF in one click.',
  href: getNavLink('/convert/word-to-pdf'),
  icon: {
    src: IconPDF,
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
  },
  categories: [ToolCategory.Convert, ToolCategory.PDF],
};

export const PDF_TO_WORD_TOOL = {
  id: 'pdf_to_word',
  name: 'PDF to Word',
  description: 'Easily convert your PDF files to Word documents in a single click.',
  href: getNavLink('/convert/pdf-to-word'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.Convert, ToolCategory.PDF],
};

export const EXCEL_TO_PDF_TOOL = {
  id: 'excel_to_pdf',
  name: 'Excel to PDF',
  description: 'Instantly convert Excel spreadsheet to PDF file online just in seconds.',
  href: getNavLink('/convert/excel-to-pdf'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
  },
  categories: [ToolCategory.Convert, ToolCategory.PDF],
};

export const JPG_TO_PNG_TOOL = {
  id: 'jpg_to_png',
  name: 'JPG to PNG',
  description:
    'This online JPG to PNG Converter is the easiest and fastest way to convert your JPGs into PNGs.',
  href: getNavLink('/convert/jpg-to-png'),
  icon: {
    src: IconCanvas,
    background: ICON_BACKGROUND[ICON_COLOR.green],
    fill: ICON_FILL[ICON_COLOR.green],
  },
  categories: [ToolCategory.Convert, ToolCategory.Image],
};

export const PNG_TO_JPG_TOOL = {
  id: 'png_to_jpg',
  name: 'PNG to JPG',
  description:
    'Convert your PNG images into JPG format in a snap. Use our easy-to-use PNG to JPG converter for free & without signing up.',
  href: getNavLink('/convert/png-to-jpg'),
  icon: {
    src: IconCanvas,
    background: ICON_BACKGROUND[ICON_COLOR.green],
    fill: ICON_FILL[ICON_COLOR.green],
  },
  categories: [ToolCategory.Convert, ToolCategory.Image],
};

export const PPT_TO_PDF_TOOL = {
  id: 'ppt_to_pdf',
  name: 'PPT to PDF',
  description: 'Convert PPT files to PDF in seconds.',
  href: getNavLink('/convert/ppt-to-pdf'),
  icon: {
    src: IconPDF,
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
  },
  categories: [ToolCategory.Convert, ToolCategory.PDF],
};

export const COLOR_PALETTE_FROM_IMAGE = {
  id: 'color_palette_from_image',
  name: 'Color Palette from Image',
  description: 'Upload an image and generate a color palette with a single click.',
  href: getNavLink('/color/palette-from-image'),
  icon: {
    src: IconEditPhoto,
    background: ICON_BACKGROUND[ICON_COLOR.indigo],
    fill: ICON_FILL[ICON_COLOR.indigo],
  },
  categories: [ToolCategory.Image, ToolCategory.Color],
};

export const MP4_MP3_TOOL = {
  id: 'mp4_to_mp3',
  name: 'MP4 to MP3',
  description: 'Convert any MP4 video to MP3 audio files in seconds.',
  href: getNavLink('/convert/mp4-to-mp3'),
  icon: {
    src: IconAddVideo,
    background: ICON_BACKGROUND[ICON_COLOR.yellow],
    fill: ICON_FILL[ICON_COLOR.yellow],
  },
  categories: [ToolCategory.Convert, ToolCategory.Video],
};

export const SHARPEN_IMAGE = {
  id: 'sharpen_image',
  name: 'Sharpen Image',
  description:
    'Take blurry or grainy images and create sharper better looking images to use anywhere.',
  href: getNavLink('/image/sharpen'),
  widgetUrl: getNavLink('/widget/image-sharpen'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.purple],
    fill: ICON_FILL[ICON_COLOR.purple],
    src: IconAITools,
  },
  categories: [ToolCategory.Image, ToolCategory.Design],
};

export const PHOTO_CAPTION = {
  id: 'photo_caption',
  name: 'Add Text to Photo',
  description:
    'Make your photo’s pop by adding text in fancy and cool fonts before publishing anywhere.',
  href: getNavLink('/text/add-text-to-photo'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.magenta],
    fill: ICON_FILL[ICON_COLOR.magenta],
    src: IconTextStaff,
  },
  categories: [ToolCategory.Image, ToolCategory.Design, ToolCategory.Text],
};

export const SVG_TO_PNG_TOOL = {
  id: 'svg_to_png',
  name: 'SVG to PNG',
  description:
    'Convert your SVGs into PNG images in a snap. Use our easy-to-use SVG to PNG converter for free.',
  href: getNavLink('/convert/svg-to-png'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.green],
    fill: ICON_FILL[ICON_COLOR.green],
    src: IconCanvas,
  },
  categories: [ToolCategory.Convert, ToolCategory.Image],
};

export const AD_COPY_GENERATOR_TOOL: IToolData = {
  id: 'ads_copy_generator',
  name: 'Ad Copy',
  description:
    'Generate personalized digital ads with enterprise-grade marketing copy for any kind of business.',
  href: getNavLink('/text/ai-writer/ads-copy-generator/', true),
  buttonHref: getNavLink('/text/ads-copy-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconBlankWrite,
  },
  categories: [ToolCategory.AiWriter],
};

export const SOCIAL_MEDIA_BIO_TOOL = {
  id: 'social_media_bio_generator',
  name: 'Social Media Bio',
  description:
    'Create a unique and customized social media bio for all major social media platforms in minutes for personal or professional needs.',
  href: getNavLink('/text/ai-writer/bio-generator/', true),
  buttonHref: getNavLink('/text/bio-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconFaceMask,
  },
  categories: [ToolCategory.AiWriter],
};

export const REPHRASER_TOOL = {
  id: 'rephraser_generator',
  name: 'Rephraser',
  description:
    'Easily and quickly rephrase copy into unique content for academic, SEO, marketing, and other uses.',
  href: getNavLink('/text/ai-writer/paraphrase/', true),
  buttonHref: getNavLink('/text/paraphrase'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconRefresh,
  },
  hasFindOutMoreButton: true,
  categories: [ToolCategory.AiWriter],
};
export const MARKETING_SLOGAN_TOOL = {
  id: 'marketing_slogan_generator',
  name: 'Slogan Generator',
  description:
    'Create a memorable and catchy slogan for your brand or business in seconds that will help users remember you easily.',
  href: getNavLink('/text/ai-writer/slogan-maker', true),
  buttonHref: getNavLink('/text/slogan-maker'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconEmail,
  },
  hasFindOutMoreButton: true,
  categories: [ToolCategory.AiWriter],
};
export const BUSINESS_NAME_TOOL = {
  id: 'business_name_generator',
  name: 'Business Name Ideas',
  description:
    'Find great names for your new buiness ventures with this simple tool; describe your business and have results in moments.',
  href: getNavLink('/text/ai-writer/business-name-generator/', true),
  buttonHref: getNavLink('/text/business-name-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconCreditCard,
  },
  categories: [ToolCategory.AiWriter],
};
export const INSTAGRAM_HASHTAG_GENERATOR_TOOL = {
  id: 'instagram_hashtag_generator',
  name: 'Hashtag Generator',
  description:
    'Find the best hashtags for your new post which can generate additional clicks and views.',
  href: getNavLink('/text/ai-writer/instagram-hashtag-generator', true),
  buttonHref: getNavLink('/text/instagram-hashtag-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconHashtag,
  },
  categories: [ToolCategory.AiWriter],
};
export const PRODUCT_DESCRIPTION_GENERATOR_TOOL = {
  id: 'product_description_generator',
  name: 'Product Descriptions',
  description:
    'Create the perfect description for your products to attract and intrigue your customers to click and buy.',
  href: getNavLink('/text/ai-writer/product-description-generator/', true),
  buttonHref: getNavLink('/text/product-description-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconFonts,
  },
  categories: [ToolCategory.AiWriter],
};

export const INSTAGRAM_CAPTION_TOOL = {
  id: 'instagram_caption',
  name: 'Image Caption Generator',
  description:
    'Every good Instagram post needs a catchy caption to increase engagement on your post and profile. ',
  href: getNavLink('/text/ai-writer/instagram-caption-generator/', true),
  buttonHref: getNavLink('/text/instagram-caption-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconInstagram,
  },
  isNew: true,
  categories: [ToolCategory.AiWriter],
};

export const LANDING_PAGE_OUTLINE_TOOL = {
  id: 'landing_page_outline',
  name: 'Outline Generator',
  description:
    'Landing page outlines improve the page structure and readability, increasing conversions.',
  href: getNavLink('/text/ai-writer/landing-page-outline/', true),
  buttonHref: getNavLink('/text/outline-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconMonitoring,
  },
  categories: [ToolCategory.AiWriter],
};

export const META_DATA_MAKER_TOOL = {
  id: 'meta_data_maker',
  name: 'Meta Description Generator',
  description:
    'Nowadays, AI generates better meta data copy than most people, so what are you waiting for?',
  href: getNavLink('/text/ai-writer/meta-description-generator/', true),
  buttonHref: getNavLink('/text/meta-description-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconEmbedCode,
  },
  isNew: true,
  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const SUMMARIZER_TOOL = {
  id: 'summarizer',
  name: 'Summarizer',
  description:
    'A true lifesaver. Save time and quickly summarize articles, social posts, essays, documents, or websites.',
  href: getNavLink('/text/ai-writer/summarizer/', true),
  buttonHref: getNavLink('/text/summarizer'),
  isNew: true,
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconList,
  },

  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const TRANSLATOR_TOOL = {
  id: 'translator',
  name: 'Translator',
  description:
    'Easily translate your text to the most popular languages online. Swap, rephrase, and copy your translated text. ',
  href: getNavLink('/text/ai-writer/translator/', true),
  isNew: true,
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconMoveToFolder,
  },

  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const ESSAY_WRITER_TOOL = {
  id: 'essay',
  name: 'Essay Writer',
  description:
    'Essays are tough, so we would like to help. Not to replace, but to make better quality content.',
  href: getNavLink('/text/ai-writer/essay-writer/', true),
  isNew: true,
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconFlyer,
  },

  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const EMAIL_WRITER_TOOL = {
  id: 'email_writer',
  name: 'Email Writer',
  description:
    "Lots of people spend hours per day sending emails. It's time to become more productive and send more emails faster.",
  href: getNavLink('/text/ai-writer/email-writer/', true),
  buttonHref: getNavLink('/text/email-writer'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconEmail,
  },
  isNew: true,
  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const IDEA_GENERATOR_TOOL = {
  id: 'idea_generator',
  name: 'Idea Generator',
  description:
    'This idea generator creates the ideas for you - No matter the topic. Give it a try!',
  href: getNavLink('/text/ai-writer/idea-generator/', true),
  buttonHref: getNavLink('/text/idea-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconLearn,
  },
  isNew: true,
  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const ARTICLE_WRITER_TOOL = {
  id: 'article_writer',
  name: 'Article Writer',
  description:
    'Use this tool to write the articles for you. Customize the input as much as you want to create high-quality SEO content.',
  href: getNavLink('/text/ai-writer/article-writer/', true),
  buttonHref: getNavLink('/text/ai-article-writer'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconBlogBanner,
  },
  isNew: true,
  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};
export const STORY_GENERATOR_TOOL = {
  id: 'story_generator',
  name: 'Story Generator',
  description:
    'A story can sell, explain, or visualize a thought or concept. Let us help you tell a good one!',
  href: getNavLink('/text/ai-writer/story-generator/', true),
  buttonHref: getNavLink('/text/ai-story-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconBlog,
  },
  isNew: true,
  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const TITLE_GENERATOR_TOOL = {
  id: 'title_generator',
  name: 'Title Generator',
  description:
    "Without a catchy title, you won't catch the attention. Let this tool generate the titles for you. They are often better.",
  href: getNavLink('/text/ai-writer/title-generator/', true),
  buttonHref: getNavLink('/text/ai-title-generator'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconEmailHeader,
  },
  isNew: true,
  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const GRAMMAR_CHECKER_TOOL = {
  id: 'grammar_checker',
  name: 'Grammar Checker',
  description: "Quickly check your text's grammar to ensure you are doing everything correctly.",
  href: getNavLink('/text/ai-writer/grammar-checker/', true),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconNoResult,
  },
  isNew: true,
  categories: [ToolCategory.Text, ToolCategory.AiWriter],
};

export const COPYWRITER_TOOLS: IToolData[] = [
  ARTICLE_WRITER_TOOL,
  TITLE_GENERATOR_TOOL,
  IDEA_GENERATOR_TOOL,
  GRAMMAR_CHECKER_TOOL,
  STORY_GENERATOR_TOOL,
  AD_COPY_GENERATOR_TOOL,
  SOCIAL_MEDIA_BIO_TOOL,
  REPHRASER_TOOL,
  MARKETING_SLOGAN_TOOL,
  BUSINESS_NAME_TOOL,
  INSTAGRAM_HASHTAG_GENERATOR_TOOL,
  PRODUCT_DESCRIPTION_GENERATOR_TOOL,
  INSTAGRAM_CAPTION_TOOL,
  LANDING_PAGE_OUTLINE_TOOL,
  META_DATA_MAKER_TOOL,
  SUMMARIZER_TOOL,
  TRANSLATOR_TOOL,
  ESSAY_WRITER_TOOL,
  EMAIL_WRITER_TOOL,
];

export const AI_WRITER_TOOL = {
  id: 'copy_writer',
  name: 'AI Writer',
  description:
    'An AI writing generator can help you easily create effective content for any purpose in moments.',
  href: getNavLink('/text/ai-writer'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconTextStaff,
  },
  categories: [ToolCategory.Text],
  subCategories: [
    { label: 'All', id: 'all', href: getNavLink('/text/ai-writer') },
    ...COPYWRITER_TOOLS.map((s) => ({
      label: s.name,
      href: s.buttonHref || s.href,
      id: s.id,
    })),
  ],
};

export const VIDEO_TOOLS: IToolData[] = [
  MP4_MP3_TOOL,
  MOV_TO_MP4_TOOL,
  MKV_TO_MP4_TOOL,
  WEBM_TO_MP4_TOOL,
  CROP_VIDEO_TOOL,
  TRIM_VIDEO_TOOL,
];

export const PDF_TOOLS: IToolData[] = [
  MERGE_PDF_TOOL,
  COMPRESS_PDF_TOOL,
  SPLIT_PDF_TOOL,
  WORD_TO_PDF_TOOL,
  PNG_TO_PDF_TOOL,
  PPT_TO_PDF_TOOL,
  PDF_TO_JPG_TOOL,
  JPG_TO_PDF_TOOL,
  PDF_TO_WORD_TOOL,
  EXCEL_TO_PDF_TOOL,
];

export const IMAGE_TOOLS: IToolData[] = [
  PROFILE_PICTURE_TOOL,
  BACKGROUND_REMOVER_TOOL,
  SHARPEN_IMAGE,
  COMPRESS_IMAGE_TOOL,
  IMAGE_ROTATE_TOOL,
  RESIZE_IMAGE_TOOL,
  PHOTO_CAPTION,
  IMAGE_CROP_TOOL,
  PNG_TO_SVG_TOOL,
  HEIC_TO_JPG_TOOL,
  JPG_TO_PNG_TOOL,
  PNG_TO_JPG_TOOL,
  COLOR_PALETTE_FROM_IMAGE,
  SVG_TO_PNG_TOOL,
  IMAGE_MIRROR_TOOL,
];

export const DESIGN_TOOLS: IToolData[] = [
  BACKGROUND_REMOVER_TOOL,
  PROFILE_PICTURE_TOOL,
  CALENDAR_MAKER_TOOL,
  COLOR_PALETTE_TOOL,
  SHARPEN_IMAGE,
  PHOTO_CAPTION,
  ADMAKER_TOOL,
];

export const TEXT_TOOLS: IToolData[] = [
  FONT_GENERATOR_TOOL,
  EMOJIS_TOOL,
  SYMBOLS_TOOL,
  AI_WRITER_TOOL,
  WORD_COUNT_TOOL,
  PHOTO_CAPTION,
];

export const AI_WRITER_TOOLS: IToolData[] = [
  IDEA_GENERATOR_TOOL,
  TITLE_GENERATOR_TOOL,
  ARTICLE_WRITER_TOOL,
  STORY_GENERATOR_TOOL,
  AD_COPY_GENERATOR_TOOL,
  SOCIAL_MEDIA_BIO_TOOL,
  REPHRASER_TOOL,
  MARKETING_SLOGAN_TOOL,
  BUSINESS_NAME_TOOL,
  INSTAGRAM_HASHTAG_GENERATOR_TOOL,
  PRODUCT_DESCRIPTION_GENERATOR_TOOL,
  EMAIL_WRITER_TOOL,
  INSTAGRAM_CAPTION_TOOL,
  LANDING_PAGE_OUTLINE_TOOL,
  META_DATA_MAKER_TOOL,
  SUMMARIZER_TOOL,
  TRANSLATOR_TOOL,
  ESSAY_WRITER_TOOL,
];

export const COLOR_TOOLS: IToolData[] = [
  COLOR_PICKER_TOOL,
  COLOR_WHEEL_TOOL,
  COLOR_CODES_TOOL,
  COLOR_PALETTE_TOOL,
  COLOR_PALETTE_FROM_IMAGE,
];

export const CONVERT_TOOLS: IToolData[] = [
  JPG_TO_PDF_TOOL,
  HEIC_TO_JPG_TOOL,
  PDF_TO_WORD_TOOL,
  PNG_TO_JPG_TOOL,
  PDF_TO_JPG_TOOL,
  EXCEL_TO_PDF_TOOL,
  MOV_TO_MP4_TOOL,
  WORD_TO_PDF_TOOL,
  PNG_TO_SVG_TOOL,
  PNG_TO_PDF_TOOL,
  WEBM_TO_MP4_TOOL,
  MKV_TO_MP4_TOOL,
  JPG_TO_PNG_TOOL,
  PPT_TO_PDF_TOOL,
  MP4_MP3_TOOL,
  SVG_TO_PNG_TOOL,
];

export const ALL_TOOLS_CATEGORY = {
  value: ToolCategory.All,
  name: getToolCategoryName(ToolCategory.All),
  href: getNavLink(''),
};

export const DESIGN_TOOLS_CATEGORY = {
  value: ToolCategory.Design,
  name: getToolCategoryName(ToolCategory.Design),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.purple],
    fill: ICON_FILL[ICON_COLOR.purple],
    src: IconMonitoring,
    width: 24,
    height: 24,
  },
  href: getNavLink('/design'),
  toolsCount: DESIGN_TOOLS.length,
};

export const VIDEO_TOOLS_CATEGORY = {
  value: ToolCategory.Video,
  name: getToolCategoryName(ToolCategory.Video),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.yellow],
    fill: ICON_FILL[ICON_COLOR.yellow],
    src: IconExportVideo,
    width: 24,
    height: 24,
  },
  href: getNavLink('/video'),
  toolsCount: VIDEO_TOOLS.length,
};

export const TEXT_TOOLS_CATEGORY = {
  value: ToolCategory.Text,
  name: getToolCategoryName(ToolCategory.Text),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.blue],
    fill: ICON_FILL[ICON_COLOR.blue],
    src: IconFonts,
    width: 24,
    height: 24,
  },
  href: getNavLink('/text'),
  toolsCount: TEXT_TOOLS.length,
};

export const COLOR_TOOLS_CATEGORY = {
  value: ToolCategory.Color,
  name: getToolCategoryName(ToolCategory.Color),
  href: getNavLink('/color'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.indigo],
    fill: ICON_FILL[ICON_COLOR.indigo],
    src: IconColorPicker,
    width: 20,
    height: 20,
  },
  toolsCount: COLOR_TOOLS.length,
};

export const CONVERT_TOOLS_CATEGORY = {
  value: ToolCategory.Convert,
  name: getToolCategoryName(ToolCategory.Convert),
  href: getNavLink('/convert'),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.green],
    fill: ICON_FILL[ICON_COLOR.green],
    src: IconRatio4x3,
    width: 24,
    height: 36,
  },
  toolsCount: CONVERT_TOOLS.length,
};

export const PDF_TOOLS_CATEGORY = {
  value: ToolCategory.PDF,
  name: getToolCategoryName(ToolCategory.PDF),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.red],
    fill: ICON_FILL[ICON_COLOR.red],
    src: IconPDF,
    width: 24,
    height: 24,
  },
  href: getNavLink('/pdf'),
  toolsCount: PDF_TOOLS.length,
};

export const IMAGE_TOOLS_CATEGORY = {
  value: ToolCategory.Image,
  name: getToolCategoryName(ToolCategory.Image),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.magenta],
    fill: ICON_FILL[ICON_COLOR.magenta],
    src: IconAITools,
    width: 24,
    height: 24,
  },
  href: getNavLink('/image'),
  toolsCount: IMAGE_TOOLS.length,
};

export const AI_WRITER_TOOLS_CATEGORY = {
  value: ToolCategory.AiWriter,
  name: getToolCategoryName(ToolCategory.AiWriter),
  icon: {
    background: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fill: ICON_FILL[ICON_COLOR.lightGreen],
    src: IconAI,
    width: 24,
    height: 24,
  },
  href: getNavLink('/text/ai-writer'),
  toolsCount: 17,
};

export const ALL_TOOLS: IToolData[] = [
  ADMAKER_TOOL,
  ...AI_WRITER_TOOLS,
  IMAGE_ROTATE_TOOL,
  IMAGE_CROP_TOOL,
  FONT_GENERATOR_TOOL,
  AI_WRITER_TOOL,
  BACKGROUND_REMOVER_TOOL,
  PROFILE_PICTURE_TOOL,
  JPG_TO_PDF_TOOL,
  EMOJIS_TOOL,
  MERGE_PDF_TOOL,
  PDF_TO_JPG_TOOL,
  PDF_TO_WORD_TOOL,
  WORD_TO_PDF_TOOL,
  WORD_COUNT_TOOL,
  SYMBOLS_TOOL,
  CALENDAR_MAKER_TOOL,
  COMPRESS_PDF_TOOL,
  COLOR_PICKER_TOOL,
  COLOR_WHEEL_TOOL,
  COLOR_CODES_TOOL,
  HEIC_TO_JPG_TOOL,
  SPLIT_PDF_TOOL,
  COLOR_PALETTE_TOOL,
  COMPRESS_IMAGE_TOOL,
  EXCEL_TO_PDF_TOOL,
  RESIZE_IMAGE_TOOL,
  MP4_MP3_TOOL,
  PNG_TO_PDF_TOOL,
  JPG_TO_PNG_TOOL,
  PNG_TO_JPG_TOOL,
  MOV_TO_MP4_TOOL,
  PPT_TO_PDF_TOOL,
  MKV_TO_MP4_TOOL,
  WEBM_TO_MP4_TOOL,
  SVG_TO_PNG_TOOL,
  COLOR_PALETTE_FROM_IMAGE,
  CROP_VIDEO_TOOL,
  TRIM_VIDEO_TOOL,
  SHARPEN_IMAGE,
  PNG_TO_SVG_TOOL,
  PHOTO_CAPTION,
  IMAGE_MIRROR_TOOL,
];

export const toolCategories: IToolCategory[] = [
  ALL_TOOLS_CATEGORY,
  TEXT_TOOLS_CATEGORY,
  AI_WRITER_TOOLS_CATEGORY,
  IMAGE_TOOLS_CATEGORY,
  VIDEO_TOOLS_CATEGORY,
  DESIGN_TOOLS_CATEGORY,
  COLOR_TOOLS_CATEGORY,
  CONVERT_TOOLS_CATEGORY,
  PDF_TOOLS_CATEGORY,
];

const getOtherToolsForCategory = (category: ToolCategory) =>
  toolCategories.filter(({ value }) => value !== category && value !== ToolCategory.All);

export const OTHER_TOOLS_DESIGN = getOtherToolsForCategory(ToolCategory.Design);

export const OTHER_TOOLS_VIDEO = getOtherToolsForCategory(ToolCategory.Video);

export const OTHER_TOOLS_TEXT = getOtherToolsForCategory(ToolCategory.Text);

export const OTHER_TOOLS_COLOR = getOtherToolsForCategory(ToolCategory.Color);

export const OTHER_TOOLS_CONVERT = getOtherToolsForCategory(ToolCategory.Convert);

export const OTHER_TOOLS_PDF = getOtherToolsForCategory(ToolCategory.PDF);

export const OTHER_TOOLS_IMAGE = getOtherToolsForCategory(ToolCategory.Image);

export const OTHER_TOOLS_AI_WRITER = getOtherToolsForCategory(ToolCategory.AiWriter);

export const TOOLS_BY_CATEGORY = {
  [ToolCategory.Design]: DESIGN_TOOLS,
  [ToolCategory.Video]: VIDEO_TOOLS,
  [ToolCategory.Text]: TEXT_TOOLS,
  [ToolCategory.Color]: COLOR_TOOLS,
  [ToolCategory.Convert]: CONVERT_TOOLS,
  [ToolCategory.PDF]: PDF_TOOLS,
  [ToolCategory.Image]: IMAGE_TOOLS,
  [ToolCategory.AiWriter]: AI_WRITER_TOOLS,
};
